import { Component, ElementRef, Inject, Injectable, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NxAutocompleteSelectedEvent } from '@aposin/ng-aquila/autocomplete';
import { EmployeeFormatPipe } from '@core/pipes/employee-format.pipe';
import { CommonService } from '@core/services/common.service';
import { EmployeeService } from '@core/services/employee.service';
import { TravelMemberService } from '@core/services/travel-member.service';
import { MaTariffList } from '@shared/models/ma-tariff-list';
import { Employee } from '@shared/models/policies/detail/employee/employee';
import { InsuredTripPartner } from '@shared/models/policies/detail/insured-trip/insured-trip-partner';
import { InsuredTripRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-request';
import moment, { isMoment, Moment } from 'moment';
import { InsuredTripsBaseComponent } from '../insured-trips-base.component';
import { AlertService } from '@core/services/alert.service';
import { InsuredTripListRequest } from '@shared/models/policies/detail/insured-trip/insured-trip-list-request';
import { InsuredTrip, InsuredTrips } from '@shared/models/policies/detail/insured-trip/insured-trip';
import { TranslateService } from '@ngx-translate/core';
import { NX_DATE_LOCALE, NxDateAdapter, NxDatepickerInputEvent, NxDatepickerIntl } from '@aposin/ng-aquila/datefield';
import { LocaleService } from '@core/services/locale.service';

@Injectable()
export class MyIntl extends NxDatepickerIntl {
    constructor(private translateService: TranslateService) {
        super();
        this.switchToMonthViewLabel = this.translateService.instant('common.date.month-view');
        this.switchToMultiYearViewLabel = this.translateService.instant('common.date.date-view');
    }
}

@Component({
    selector: 'app-new-insured-trip',
    templateUrl: './new-insured-trip.component.html',
    styleUrls: ['./new-insured-trip.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class NewInsuredTripComponent extends InsuredTripsBaseComponent implements OnInit {
    @ViewChild('employeesInput') employeesInput: ElementRef<any>;

    moduleValues = {};
    rate: number;
    tariff: string;
    premium: number;
    formNewInsuredTrip: FormGroup;

    private autocompleteEmployees: string[] = [];
    private employees: Array<Employee> = [];
    private policyNumber: string;
    private policyStatus: string;
    private tariffList: MaTariffList[] = [];

    private insuredTrips: InsuredTrips;

    public dateEndStart: Moment;

    public showHasTravel = false;
    public hasTravel: {[name: string]: {
        list: InsuredTrip[];
        allDataFlag: boolean;
    }} = {};

    constructor(
        public commonService: CommonService,
        private employeeFormat: EmployeeFormatPipe,
        private employeeService: EmployeeService,
        private travelMemberService: TravelMemberService,
        private route: ActivatedRoute,
        private router: Router,
        private alertService: AlertService,
        private translateService: TranslateService,
        private localeService: LocaleService,
        @Inject(NX_DATE_LOCALE) private nxDateLocale: string,
        @Inject(NxDatepickerIntl) private intl: NxDatepickerIntl,
    ) {
        super(commonService);
        
        this.dateEndStart = moment();

        this.translateService.get('common.date.month-view').subscribe( x => {
            this.intl.switchToMonthViewLabel = x;
        })

        this.translateService.get('common.date.date-view').subscribe( x => {
            this.intl.switchToMultiYearViewLabel = x;
        })

        translateService.onLangChange.subscribe(x => {
            let lang = this.localeService.languages.find(y => y.code.toLowerCase() == x.lang);
            if (lang && lang.iso) {
                intl.switchToMonthViewLabel = translateService.instant('common.date.month-view');
                intl.switchToMultiYearViewLabel = translateService.instant('common.date.date-view');
                this.nxDateLocale = lang.iso;
            }
        });
    }

    ngOnInit(): void {
        this.initForm();
        this.employeeService.getEmployees(this.getLoggedUser().vatNumber).subscribe(employees => {
            this.employees = employees;
            employees.forEach((employee) => {
                if (employee.canceledDate == undefined || employee.canceledDate == null) {
                    this.autocompleteEmployees.push(employee.name + ';' + employee.surname + ';' + employee.dateOfBirth);
                }
            });
        });

        this.route.params.subscribe(params => {
            if (params.policyNumber != undefined && params.policyNumber.length > 0 &&
                params.status != undefined && params.status.length > 0) {
                this.policyNumber = params.policyNumber;
                this.policyStatus = params.status;

                this.travelMemberService.getTravelMaInsurance(this.policyNumber).subscribe(maInsurance => {
                    maInsurance.tariffList.forEach((tariffList) => {
                        (this.tariffList.find(tariff =>
                            tariffList.zone + this.moduleNameByTarrif(tariffList.tariff) ==
                            tariff.zone + this.moduleNameByTarrif(tariff.tariff)
                        )) == undefined ? this.initValues(tariffList) : null;
                    });
                });

                const insuredTripListRequest = new InsuredTripListRequest();
                insuredTripListRequest.statusId = '4'; // active status

                this.travelMemberService.postTravelMemberList(this.policyNumber, insuredTripListRequest)
                    .subscribe((insuredTrips) => {
                        this.insuredTrips = insuredTrips;
                    });
            }
        });
    }

    private initForm() {

        (this.formNewInsuredTrip = new FormGroup({
            module: new FormControl(null, Validators.required),
            zone: new FormControl(null, Validators.required),
            dateBegin: new FormControl(null, Validators.required),
            dateEnd: new FormControl(null, Validators.required),
            employees: new FormArray([], Validators.required)
        })).valueChanges.subscribe((formValue: object) => {
            for (const key in formValue) {
                if (['module', 'zone'].includes(key)) {

                    let tariffItem = this.tariffList.find((tariffItem) =>
                        this.moduleNameByTarrif(tariffItem.tariff) == formValue['module'] &&
                        tariffItem.zone == formValue['zone']
                    );

                    if (tariffItem != undefined) {
                        this.tariff = tariffItem.tariff;
                        this.rate = tariffItem.rate;
                    }
                }
            }

            this.premium = null;
            if (this.formNewInsuredTrip.valid) {
                this.premium = (moment(formValue['dateEnd']).diff(formValue['dateBegin'], 'days') + 1)
                    * this.rate * this.formNewInsuredTrip.getRawValue().employees.length;
            }

            return true;
        });
    }

    private initValues(tariffList: MaTariffList) {
        this.tariffList.push(tariffList);
        let schemeNameMapping = this.moduleNameByTarrif(tariffList.tariff);
        this.moduleValues[schemeNameMapping] = schemeNameMapping;
    }

    changedDateStart($event: NxDatepickerInputEvent<Moment>) {
        if($event.value.isValid()){
            this.dateEndStart = $event.value;
        }
    }

    filterDateBegin = (date: Moment): boolean => {
        if (date.isSameOrAfter(moment.now(), 'days') && (
            isMoment(this.formNewInsuredTrip.value.dateEnd) ?
                date.isSameOrBefore(this.formNewInsuredTrip.value.dateEnd, 'days') : true
        )) {
            return true;
        }

        return false;
    };

    filterDateEnd = (date: Moment): boolean => {
        if (date.isSameOrAfter(moment.now(), 'days') && (
            isMoment(this.formNewInsuredTrip.value.dateBegin) ?
                date.isSameOrAfter(this.formNewInsuredTrip.value.dateBegin) : true
        )) {
            return true;
        }

        return false;
    };

    getEmployees(searchValue: string, transform: boolean = true): Array<string> {
        if (!searchValue) {
            return this.autocompleteEmployees.filter((item) => {
                let found = false;
                (this.formNewInsuredTrip.controls.employees.value as Array<object>).forEach((eItem) => {
                    if(this.employeeFormat.transform(item.toLowerCase()).indexOf(eItem['employee'].toLowerCase()) != -1){
                        found = true;
                    }
                })

                if(!found){
                    return item;
                }

                return false;
            });
        }

        return this.autocompleteEmployees.filter((item: string) => {
            let found = false;
            (this.formNewInsuredTrip.controls.employees.value as Array<object>).forEach((eItem) => {
                if(this.employeeFormat.transform(item.toLowerCase()).indexOf(eItem['employee'].toLowerCase()) != -1){
                    found = true;
                }
            })

            if(!found){
                return this.employeeFormat.transform(item.toLowerCase()).indexOf(searchValue.toLowerCase()) != -1;
            }

            return false;
        });
    }

    selectEmployee(selection: NxAutocompleteSelectedEvent) {
        (this.formNewInsuredTrip.get('employees') as FormArray).push(
            new FormGroup({
                employee: new FormControl({ value: selection.option.value, disabled: true })
            })
        );

        selection.option.deselect();
        this.employeesInput.nativeElement.value = '';
    }

    deselectEmployee(index: number) {
        if ((this.formNewInsuredTrip.get('employees') as FormArray).value[index] != undefined) {
            (this.formNewInsuredTrip.get('employees') as FormArray).removeAt(index);
        }
    }

    submit($event: SubmitEvent) {
        if (!this.formNewInsuredTrip.valid) {
            return;
        }

        let insuredTripRequest = new InsuredTripRequest();
        insuredTripRequest.dateBegin = moment(this.formNewInsuredTrip.value.dateBegin).format();
        insuredTripRequest.dateEnd = moment(this.formNewInsuredTrip.value.dateEnd).format();
        insuredTripRequest.dateSigned = moment().format();
        insuredTripRequest.zone = this.formNewInsuredTrip.value.zone;
        insuredTripRequest.premium = this.premium;
        insuredTripRequest.tariff = this.tariff;
        insuredTripRequest.partner = new Array<InsuredTripPartner>();

        (this.formNewInsuredTrip.getRawValue().employees as Array<object>).forEach((employee: {
            [employee: string]: string;
        }) => {
            let employeeAttrs = this.employees.find(emp =>
                this.employeeFormat.transform(emp.name + ';' + emp.surname + ';' + emp.dateOfBirth) == employee.employee
            );

            insuredTripRequest.partner.push({
                firstName: employeeAttrs.name,
                surname: employeeAttrs.surname,
                dateOfBirth: moment(employeeAttrs.dateOfBirth).format()
            });
        });

        this.travelMemberService.insertTravelMember(this.policyNumber, insuredTripRequest)
            .subscribe((insuredTripResponses) => {
                this.showHasTravel = false;
                if (insuredTripResponses.worker) {
                    this.router.navigate([`/moje-poistky/${this.policyStatus}/${this.policyNumber}/poistene-cesty`]);
                    return;
                }

                this.hasTravel = {};

                const firstKey = Object.keys(insuredTripResponses.results)[0];
                if(insuredTripRequest.partner.length === 1 && Object.keys(insuredTripResponses.results).length === 1){
                    if (insuredTripResponses.results[firstKey].policyNumber) {
                        this.translateService.get('policy.insured-trips.show-new-travel', {
                            yes: `<a class="underlined" href="${this.commonService.getBaseHref()}moje-poistky/${this.policyStatus}/${this.policyNumber}/poistene-cesty/${insuredTripResponses.results[firstKey].policyNumber}">${this.translateService.instant('common.yes')}</a>`,
                            no: `<a class="underlined" href="${this.commonService.getBaseHref()}moje-poistky/${this.policyStatus}/${this.policyNumber}/poistene-cesty">${this.translateService.instant('common.no')}</a>`
                        }).subscribe(msg => {
                            this.alertService.info(msg);
                        });
                        return;
                    }
                }
                
                if(insuredTripResponses.results[firstKey].hasTravel.list.length){
                    this.showHasTravel = true;
                    Object.keys(insuredTripResponses.results).forEach(key => {
                        this.hasTravel[key] = insuredTripResponses.results[key].hasTravel
                    })
                    return;
                }

                this.router.navigate([`/moje-poistky/${this.policyStatus}/${this.policyNumber}/poistene-cesty`]);
            });
    }

    getHasTravelLink(travelPolicyNumber) {
        return this.commonService.getBaseHref() + `moje-poistky/${this.policyStatus}/${this.policyNumber}/poistene-cesty/${travelPolicyNumber}`;
    }

    formatKeyName(keyName: string){
        let parsed = keyName.split(';');
        parsed[parsed.length - 1] = moment(parsed[parsed.length - 1]).format('YYYY-MM-DD');

        return parsed.join(' ');
    }
}
